(() => {
  'use strict';

  const modulo = (value, length) => ((value % length) + length) % length;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  // Corrige, em runtime, o tamanho visual do tênis para ficar consistente
  // entre cores, mesmo quando os PNGs originais têm margens transparentes
  // bem diferentes entre si (object-fit: contain só enxerga o canvas
  // inteiro, não a área realmente opaca da imagem).
  const SHOE_CONTENT_CACHE = new Map();
  const SHOE_TARGET_FRACTION = 0.8;
  const SHOE_SCAN_MAX_SIDE = 220;
  const SHOE_ALPHA_THRESHOLD = 12;
  const SHOE_CONTENT_PADDING = 0.04;

  function measureShoeContentBox(measureImg) {
    const src = measureImg.currentSrc || measureImg.src;
    if (!src) return Promise.resolve(null);
    if (SHOE_CONTENT_CACHE.has(src)) return SHOE_CONTENT_CACHE.get(src);

    const promise = new Promise((resolve) => {
      const finish = (box) => resolve(box);

      const run = () => {
        try {
          const naturalWidth = measureImg.naturalWidth;
          const naturalHeight = measureImg.naturalHeight;
          if (!naturalWidth || !naturalHeight) {
            finish(null);
            return;
          }

          const scale = Math.min(1, SHOE_SCAN_MAX_SIDE / Math.max(naturalWidth, naturalHeight));
          const scanWidth = Math.max(1, Math.round(naturalWidth * scale));
          const scanHeight = Math.max(1, Math.round(naturalHeight * scale));

          const canvas = document.createElement('canvas');
          canvas.width = scanWidth;
          canvas.height = scanHeight;
          const context = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
          if (!context) {
            finish(null);
            return;
          }

          context.clearRect(0, 0, scanWidth, scanHeight);
          context.drawImage(measureImg, 0, 0, scanWidth, scanHeight);

          const { data } = context.getImageData(0, 0, scanWidth, scanHeight);
          let minX = scanWidth;
          let minY = scanHeight;
          let maxX = -1;
          let maxY = -1;

          for (let y = 0; y < scanHeight; y += 1) {
            for (let x = 0; x < scanWidth; x += 1) {
              const alpha = data[((y * scanWidth) + x) * 4 + 3];
              if (alpha <= SHOE_ALPHA_THRESHOLD) continue;
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }

          if (maxX < minX || maxY < minY) {
            finish(null);
            return;
          }

          let x0 = minX / scanWidth;
          let y0 = minY / scanHeight;
          let x1 = (maxX + 1) / scanWidth;
          let y1 = (maxY + 1) / scanHeight;

          const padX = (x1 - x0) * SHOE_CONTENT_PADDING;
          const padY = (y1 - y0) * SHOE_CONTENT_PADDING;
          x0 = clamp(x0 - padX, 0, 1);
          y0 = clamp(y0 - padY, 0, 1);
          x1 = clamp(x1 + padX, 0, 1);
          y1 = clamp(y1 + padY, 0, 1);

          finish({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 });
        } catch (error) {
          finish(null);
        }
      };

      if (measureImg.complete && measureImg.naturalWidth > 0) run();
      else measureImg.addEventListener('load', run, { once: true });
      measureImg.addEventListener('error', () => finish(null), { once: true });
    });

    SHOE_CONTENT_CACHE.set(src, promise);
    return promise;
  }

  function applyShoeContentFix(sourceImg, box) {
    if (!box) return false;
    const wrapper = sourceImg.parentElement;
    if (!wrapper) return false;

    const boxW = wrapper.offsetWidth;
    const boxH = wrapper.offsetHeight;
    const renderedW = sourceImg.offsetWidth;
    const renderedH = sourceImg.offsetHeight;
    if (!boxW || !boxH || !renderedW || !renderedH) return false;

    const contentCenterX = box.x + (box.w / 2);
    const contentCenterY = box.y + (box.h / 2);
    const offsetX = (contentCenterX - 0.5) * renderedW;
    const offsetY = (contentCenterY - 0.5) * renderedH;

    // Descobre qual eixo do object-fit: contain está "no limite" da caixa
    // (é esse eixo que define o tamanho do conteúdo, o outro sobra).
    const heightBound = renderedH >= boxH - 1;
    const scaleCorrection = clamp(
      heightBound
        ? (boxH * SHOE_TARGET_FRACTION) / (renderedH * box.h)
        : (boxW * SHOE_TARGET_FRACTION) / (renderedW * box.w),
      0.4,
      3.2
    );

    sourceImg.style.transformOrigin = `${(contentCenterX * 100).toFixed(2)}% ${(contentCenterY * 100).toFixed(2)}%`;
    sourceImg.style.transform = `translate(${(-offsetX).toFixed(2)}px, ${(-offsetY).toFixed(2)}px) scale(${scaleCorrection.toFixed(4)})`;
    return true;
  }

  class RS8SeriesTwo extends HTMLElement {
    constructor() {
      super();

      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onPointerCancel = this._onPointerCancel.bind(this);
      this._onBlockSelect = this._onBlockSelect.bind(this);
      this._onViewportChange = this._onViewportChange.bind(this);
      this._onMotionChange = this._onMotionChange.bind(this);
      this._onVisibilityChange = this._onVisibilityChange.bind(this);
      this._onVideoStateChange = this._onVideoStateChange.bind(this);
      this._onWindowResize = this._onWindowResize.bind(this);
      this._onGlassPointerMove = this._onGlassPointerMove.bind(this);
      this._onGlassPointerLeave = this._onGlassPointerLeave.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this._slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this._dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
      this._colorNames = Array.from(this.querySelectorAll('[data-carousel-color-name]'));
      this._carousel = this.querySelector('[data-carousel]');
      this._glassSurface = this.querySelector('[data-glass-surface]');
      this._viewport = this.querySelector('[data-carousel-viewport]');
      this._buyButton = this.querySelector('[data-carousel-buy]');
      this._status = this.querySelector('[data-carousel-status]');
      this._video = this.querySelector('[data-rs8-video]');
      this._videoToggle = this.querySelector('[data-video-toggle]');
      this._activeIndex = Math.max(0, this._slides.findIndex((slide) => slide.classList.contains('is-active')));
      this._mobileQuery = window.matchMedia('(max-width: 749px)');
      this._motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._videoAutoplayDesktop = Boolean(this._video && this._video.dataset.autoplayDesktop === 'true');
      this._videoAutoplayMobile = Boolean(this._video && this._video.dataset.autoplayMobile === 'true');
      this._videoUserPaused = !this._currentVideoAutoplayPreference();
      this._videoInView = true;
      this._videoHydrated = false;
      this._swipe = null;
      this._suppressSlideClickUntil = 0;
      this._resizeHandle = null;
      this._glassFrame = null;
      this._glassPointer = null;
      this._shoeResizeObserver = null;

      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
      this.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('visibilitychange', this._onVisibilityChange);
      window.addEventListener('resize', this._onWindowResize);

      if (this._carousel && this._glassSurface) {
        this._carousel.addEventListener('pointermove', this._onGlassPointerMove, { passive: true });
        this._carousel.addEventListener('pointerleave', this._onGlassPointerLeave);
      }

      if (this._viewport) {
        this._viewport.addEventListener('pointerdown', this._onPointerDown);
        this._viewport.addEventListener('pointermove', this._onPointerMove);
        this._viewport.addEventListener('pointerup', this._onPointerUp);
        this._viewport.addEventListener('pointercancel', this._onPointerCancel);
        this._viewport.addEventListener('lostpointercapture', this._onPointerCancel);
      }

      if (this._mobileQuery.addEventListener) {
        this._mobileQuery.addEventListener('change', this._onViewportChange);
        this._motionQuery.addEventListener('change', this._onMotionChange);
      } else {
        this._mobileQuery.addListener(this._onViewportChange);
        this._motionQuery.addListener(this._onMotionChange);
      }

      if (this._video) {
        this._video.addEventListener('play', this._onVideoStateChange);
        this._video.addEventListener('pause', this._onVideoStateChange);
        this._video.addEventListener('ended', this._onVideoStateChange);
      }

      if ('ResizeObserver' in window && (this._viewport || this._carousel)) {
        this._shoeResizeObserver = new ResizeObserver(() => this._onWindowResize());
        this._shoeResizeObserver.observe(this._viewport || this._carousel);
      }

      this.classList.add('is-enhanced');
      this._render(this._activeIndex, { announce: false });
      this._setupVideo();
      this._prepareShoeNormalization();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this._connected = false;

      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
      this.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      window.removeEventListener('resize', this._onWindowResize);
      if (this._resizeHandle) cancelAnimationFrame(this._resizeHandle);
      if (this._glassFrame) cancelAnimationFrame(this._glassFrame);
      this._shoeResizeObserver?.disconnect();
      this._shoeResizeObserver = null;

      this._slides?.forEach((slide) => {
        if (slide._rs8ShoeLayoutFrame) cancelAnimationFrame(slide._rs8ShoeLayoutFrame);
        if (slide._rs8ShoeTransitionFrame) cancelAnimationFrame(slide._rs8ShoeTransitionFrame);
        slide._rs8ShoeLayoutFrame = null;
        slide._rs8ShoeTransitionFrame = null;
      });

      if (this._carousel && this._glassSurface) {
        this._carousel.removeEventListener('pointermove', this._onGlassPointerMove);
        this._carousel.removeEventListener('pointerleave', this._onGlassPointerLeave);
      }

      if (this._viewport) {
        this._viewport.removeEventListener('pointerdown', this._onPointerDown);
        this._viewport.removeEventListener('pointermove', this._onPointerMove);
        this._viewport.removeEventListener('pointerup', this._onPointerUp);
        this._viewport.removeEventListener('pointercancel', this._onPointerCancel);
        this._viewport.removeEventListener('lostpointercapture', this._onPointerCancel);
      }

      if (this._mobileQuery) {
        if (this._mobileQuery.removeEventListener) {
          this._mobileQuery.removeEventListener('change', this._onViewportChange);
          this._motionQuery.removeEventListener('change', this._onMotionChange);
        } else {
          this._mobileQuery.removeListener(this._onViewportChange);
          this._motionQuery.removeListener(this._onMotionChange);
        }
      }

      if (this._video) {
        this._video.removeEventListener('play', this._onVideoStateChange);
        this._video.removeEventListener('pause', this._onVideoStateChange);
        this._video.removeEventListener('ended', this._onVideoStateChange);
      }

      if (this._videoObserver) this._videoObserver.disconnect();
    }

    _onClick(event) {
      const dot = event.target.closest('[data-carousel-dot]');
      if (dot && this.contains(dot)) {
        const index = Number.parseInt(dot.dataset.index || '', 10);
        if (Number.isInteger(index)) this._activate(index);
        return;
      }

      const previous = event.target.closest('[data-carousel-prev]');
      if (previous && this.contains(previous)) {
        this._navigate(-1);
        return;
      }

      const next = event.target.closest('[data-carousel-next]');
      if (next && this.contains(next)) {
        this._navigate(1);
        return;
      }

      const videoToggle = event.target.closest('[data-video-toggle]');
      if (videoToggle && this.contains(videoToggle)) {
        this._toggleVideo();
        return;
      }

      const slideLink = event.target.closest('.rs8-s2__slide-link');
      if (slideLink && performance.now() < this._suppressSlideClickUntil) {
        event.preventDefault();
      }
    }

    _onKeydown(event) {
      if (!this._carousel || !this._carousel.contains(event.target) || this._slides.length < 2) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this._navigate(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this._navigate(1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this._activate(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this._activate(this._slides.length - 1);
      }
    }

    _onPointerDown(event) {
      if (!event.isPrimary || this._slides.length < 2) return;

      this._swipe = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        currentX: event.clientX,
        startedAt: performance.now(),
        horizontal: false,
        dragged: false
      };

      if (this._viewport && this._viewport.setPointerCapture) {
        try {
          this._viewport.setPointerCapture(event.pointerId);
        } catch (_) {
          // Alguns navegadores recusam a captura em ponteiros específicos.
        }
      }
    }

    _onPointerMove(event) {
      if (!this._swipe || event.pointerId !== this._swipe.pointerId) return;

      const deltaX = event.clientX - this._swipe.startX;
      const deltaY = event.clientY - this._swipe.startY;
      this._swipe.currentX = event.clientX;

      if (!this._swipe.horizontal && Math.abs(deltaX) > 10) {
        this._swipe.horizontal = Math.abs(deltaX) > Math.abs(deltaY) * 1.08;
      }

      if (!this._swipe.horizontal) return;

      event.preventDefault();
      this._swipe.dragged = Math.abs(deltaX) > 14;
      this.classList.toggle('is-dragging', this._swipe.dragged);
      this.style.setProperty('--rs8-drag-x', `${clamp(deltaX * 0.25, -54, 54).toFixed(1)}px`);
    }

    _onPointerUp(event) {
      if (!this._swipe || event.pointerId !== this._swipe.pointerId) return;

      const deltaX = event.clientX - this._swipe.startX;
      const deltaY = event.clientY - this._swipe.startY;
      const elapsed = Math.max(1, performance.now() - this._swipe.startedAt);
      const velocity = Math.abs(deltaX) / elapsed;
      const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY) * 1.05;
      const dragged = this._swipe.dragged;

      this._releasePointer(event.pointerId);
      this._swipe = null;
      this.classList.remove('is-dragging');
      this.style.setProperty('--rs8-drag-x', '0px');

      if (dragged) this._suppressSlideClickUntil = performance.now() + 420;
      if (!isHorizontal || (Math.abs(deltaX) < 44 && velocity < 0.44)) return;

      this._navigate(deltaX < 0 ? 1 : -1);
    }

    _onPointerCancel(event) {
      if (this._swipe && event.pointerId && event.pointerId !== this._swipe.pointerId) return;
      if (this._swipe) this._releasePointer(this._swipe.pointerId);
      this._swipe = null;
      this.classList.remove('is-dragging');
      this.style.setProperty('--rs8-drag-x', '0px');
    }

    _releasePointer(pointerId) {
      if (!this._viewport || !this._viewport.releasePointerCapture || pointerId === undefined) return;

      try {
        if (this._viewport.hasPointerCapture(pointerId)) {
          this._viewport.releasePointerCapture(pointerId);
        }
      } catch (_) {
        // A captura pode já ter sido liberada pelo navegador.
      }
    }

    _onBlockSelect(event) {
      const blockElement = event.target.closest('[data-block-id]');
      const blockId = (event.detail && event.detail.blockId) || (blockElement && blockElement.dataset.blockId);
      if (!blockId) return;

      const index = this._slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (index >= 0) this._activate(index, false);
    }

    _navigate(direction) {
      if (this._slides.length < 2) return;
      this._activate(modulo(this._activeIndex + direction, this._slides.length));
    }

    _activate(index, announce = true) {
      if (!this._slides.length) return;
      const nextIndex = modulo(index, this._slides.length);
      if (nextIndex === this._activeIndex && this.classList.contains('is-ready')) return;
      this._activeIndex = nextIndex;
      this._render(nextIndex, { announce });
    }

    _render(index, { announce = true } = {}) {
      const count = this._slides.length;
      if (!count) return;

      const previousIndex = count > 2 ? modulo(index - 1, count) : -1;
      const nextIndex = count > 1 ? modulo(index + 1, count) : -1;

      this._slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        const isPrevious = count > 2 && slideIndex === previousIndex;
        const isNext = slideIndex === nextIndex;

        slide.classList.toggle('is-active', isActive);
        slide.classList.toggle('is-prev', isPrevious);
        slide.classList.toggle('is-next', isNext);
        slide.classList.toggle('is-hidden', !isActive && !isPrevious && !isNext);
        slide.setAttribute('aria-hidden', String(!isActive));

        const link = slide.querySelector('a.rs8-s2__slide-link');
        if (link) link.tabIndex = isActive ? 0 : -1;
      });

      this._dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.setAttribute('aria-current', String(isActive));
        dot.tabIndex = isActive ? 0 : -1;
      });

      const activeSlide = this._slides[index];
      const name = activeSlide.dataset.name || 'Cor';
      const productName = activeSlide.dataset.productName || 'RS8 AVANT';
      const productUrl = activeSlide.dataset.productUrl || '';
      const glassColor = activeSlide.dataset.glassColor || '#EEE9E1';
      const glassOpacity = clamp(Number.parseFloat(activeSlide.dataset.glassOpacity || '14') / 100, 0, 1);

      this.style.setProperty('--rs8-glass-color', glassColor);
      this.style.setProperty('--rs8-glass-color-opacity', String(glassOpacity));
      this.dataset.activeIndex = String(index);

      this._colorNames.forEach((colorName) => {
        colorName.textContent = name;
      });

      if (this._buyButton) {
        const buttonLabel = this._buyButton.dataset.buyLabel || 'COMPRAR';
        this._buyButton.textContent = buttonLabel;
        this._buyButton.setAttribute('aria-label', `Comprar ${productName}${name ? ` — ${name}` : ''}`);

        if (productUrl) {
          this._buyButton.href = productUrl;
          this._buyButton.removeAttribute('aria-disabled');
          this._buyButton.removeAttribute('tabindex');
        } else {
          this._buyButton.removeAttribute('href');
          this._buyButton.setAttribute('aria-disabled', 'true');
          this._buyButton.setAttribute('tabindex', '-1');
        }
      }

      if (this._status && announce) {
        this._status.textContent = `${productName}, ${name}, ${index + 1} de ${count}`;
      }

      this.dispatchEvent(new CustomEvent('rs8:color-change', {
        bubbles: true,
        detail: {
          index,
          name,
          productName,
          productUrl,
          blockId: activeSlide.dataset.blockId || ''
        }
      }));

      this._normalizeSlideShoe(activeSlide);
      if (previousIndex >= 0) this._normalizeSlideShoe(this._slides[previousIndex]);
      if (nextIndex >= 0) this._normalizeSlideShoe(this._slides[nextIndex]);
    }

    _prepareShoeNormalization() {
      const priorityIndexes = new Set([
        this._activeIndex,
        modulo(this._activeIndex - 1, Math.max(1, this._slides.length)),
        modulo(this._activeIndex + 1, Math.max(1, this._slides.length))
      ]);

      priorityIndexes.forEach((index) => this._normalizeSlideShoe(this._slides[index]));

      const prepareRest = () => {
        this._slides.forEach((slide, index) => {
          if (!priorityIndexes.has(index)) this._normalizeSlideShoe(slide);
        });
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(prepareRest, { timeout: 1600 });
      } else {
        window.setTimeout(prepareRest, 300);
      }
    }

    _normalizeSlideShoe(slide) {
      if (!slide) return;

      const sourceImg = slide.querySelector('.rs8-s2__shoe-source');
      const measureImg = slide.querySelector('.rs8-s2__shoe-measure');
      if (!sourceImg || !measureImg) return;

      if (slide._rs8ShoeBox) {
        this._applySlideShoeFix(slide);
        return;
      }

      if (slide._rs8ShoePromise) return;

      slide.dataset.shoeNormalizing = 'true';

      if (!sourceImg.complete) {
        sourceImg.addEventListener('load', () => this._applySlideShoeFix(slide), { once: true });
      }

      slide._rs8ShoePromise = measureShoeContentBox(measureImg)
        .then((box) => {
          if (!this.isConnected) return;
          if (!box) {
            slide.classList.add('is-shoe-ready');
            return;
          }

          slide._rs8ShoeBox = box;
          this._applySlideShoeFix(slide);
        })
        .finally(() => {
          slide._rs8ShoePromise = null;
          delete slide.dataset.shoeNormalizing;
        });
    }

    _applySlideShoeFix(slide, attemptsRemaining = 24) {
      if (!slide || !slide._rs8ShoeBox || !this.isConnected) return;

      const sourceImg = slide.querySelector('.rs8-s2__shoe-source');
      if (!sourceImg) return;

      const isFirstNormalization = !slide.classList.contains('is-shoe-normalized');
      if (isFirstNormalization) sourceImg.style.transition = 'none';

      if (applyShoeContentFix(sourceImg, slide._rs8ShoeBox)) {
        slide.classList.add('is-shoe-normalized', 'is-shoe-ready');
        if (slide._rs8ShoeLayoutFrame) cancelAnimationFrame(slide._rs8ShoeLayoutFrame);
        slide._rs8ShoeLayoutFrame = null;

        if (isFirstNormalization) {
          if (slide._rs8ShoeTransitionFrame) cancelAnimationFrame(slide._rs8ShoeTransitionFrame);
          slide._rs8ShoeTransitionFrame = requestAnimationFrame(() => {
            sourceImg.style.removeProperty('transition');
            slide._rs8ShoeTransitionFrame = null;
          });
        }

        return;
      }

      if (isFirstNormalization) sourceImg.style.removeProperty('transition');
      if (attemptsRemaining <= 0 || slide._rs8ShoeLayoutFrame) return;

      slide._rs8ShoeLayoutFrame = requestAnimationFrame(() => {
        slide._rs8ShoeLayoutFrame = null;
        this._applySlideShoeFix(slide, attemptsRemaining - 1);
      });
    }

    _onWindowResize() {
      if (this._resizeHandle) cancelAnimationFrame(this._resizeHandle);
      this._resizeHandle = requestAnimationFrame(() => {
        this._resizeHandle = null;
        this._slides.forEach((slide) => {
          if (slide._rs8ShoeBox) {
            this._applySlideShoeFix(slide);
          } else {
            this._normalizeSlideShoe(slide);
          }
        });
      });
    }

    _onGlassPointerMove(event) {
      if (!this._glassSurface || this._motionQuery.matches) return;

      const rect = this._glassSurface.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      this._glassPointer = {
        x: clamp((event.clientX - rect.left) / rect.width, 0, 1),
        y: clamp((event.clientY - rect.top) / rect.height, 0, 1)
      };

      if (this._glassFrame) return;
      this._glassFrame = requestAnimationFrame(() => {
        this._glassFrame = null;
        if (!this._glassPointer) return;

        const { x, y } = this._glassPointer;
        this._carousel.style.setProperty('--rs8-glass-light-x', `${(x * 100).toFixed(2)}%`);
        this._carousel.style.setProperty('--rs8-glass-light-y', `${(y * 100).toFixed(2)}%`);
        this._carousel.style.setProperty('--rs8-glass-shift-x', `${((x - 0.5) * 18).toFixed(2)}px`);
        this._carousel.style.setProperty('--rs8-glass-shift-y', `${((y - 0.5) * 14).toFixed(2)}px`);
      });
    }

    _onGlassPointerLeave() {
      if (!this._carousel) return;
      this._glassPointer = null;
      this._carousel.style.setProperty('--rs8-glass-light-x', '24%');
      this._carousel.style.setProperty('--rs8-glass-light-y', '14%');
      this._carousel.style.setProperty('--rs8-glass-shift-x', '0px');
      this._carousel.style.setProperty('--rs8-glass-shift-y', '0px');
    }

    _currentVideoAutoplayPreference() {
      return this._mobileQuery.matches ? this._videoAutoplayMobile : this._videoAutoplayDesktop;
    }

    _setupVideo() {
      if (!this._video) return;

      if ('IntersectionObserver' in window) {
        this._videoInView = false;
        this._videoObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          this._videoInView = Boolean(entry && entry.isIntersecting && entry.intersectionRatio > 0.08);

          if (!this._videoHydrated && entry && entry.isIntersecting) {
            this._hydrateVideo();
          }

          this._syncVideoPlayback();
        }, {
          rootMargin: '280px 0px',
          threshold: [0, 0.08, 0.35]
        });
        this._videoObserver.observe(this._video);
      } else {
        this._hydrateVideo();
        this._syncVideoPlayback();
      }
    }

    _hydrateVideo(force = false) {
      if (!this._video) return;

      const mobile = this._mobileQuery.matches;
      const source = mobile
        ? (this._video.dataset.mobileSrc || this._video.dataset.desktopSrc)
        : (this._video.dataset.desktopSrc || this._video.dataset.mobileSrc);
      const poster = mobile
        ? (this._video.dataset.mobilePoster || this._video.dataset.desktopPoster)
        : (this._video.dataset.desktopPoster || this._video.dataset.mobilePoster);

      if (poster && this._video.poster !== poster) this._video.poster = poster;
      if (!source) return;
      if (!force && this._video.dataset.loadedSrc === source) return;

      const wasPlaying = !this._video.paused;
      this._video.src = source;
      this._video.dataset.loadedSrc = source;
      this._video.load();
      this._videoHydrated = true;

      if (wasPlaying || (this._currentVideoAutoplayPreference() && !this._videoUserPaused)) {
        this._syncVideoPlayback();
      }
    }

    _toggleVideo() {
      if (!this._video) return;
      if (!this._videoHydrated) this._hydrateVideo();

      if (this._video.paused) {
        this._videoUserPaused = false;
        const playPromise = this._video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => this._onVideoStateChange());
        }
      } else {
        this._videoUserPaused = true;
        this._video.pause();
      }
    }

    _syncVideoPlayback() {
      if (!this._video || !this._videoHydrated) return;

      const autoplayEnabled = this._currentVideoAutoplayPreference();
      const shouldPlay = autoplayEnabled
        && !this._videoUserPaused
        && this._videoInView
        && !this._motionQuery.matches
        && document.visibilityState === 'visible';

      if (shouldPlay && this._video.paused) {
        const playPromise = this._video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => this._onVideoStateChange());
        }
      } else if (!shouldPlay && !this._video.paused && autoplayEnabled) {
        this._video.pause();
      }
    }

    _onVideoStateChange() {
      if (!this._video) return;
      const playing = !this._video.paused && !this._video.ended;
      this.classList.toggle('is-video-playing', playing);

      if (this._videoToggle) {
        this._videoToggle.setAttribute('aria-label', playing ? 'Pausar vídeo' : 'Reproduzir vídeo');
        this._videoToggle.setAttribute('aria-pressed', String(playing));
      }
    }

    _onViewportChange() {
      this._videoUserPaused = !this._currentVideoAutoplayPreference();
      this._hydrateVideo(true);
      this._syncVideoPlayback();
    }

    _onMotionChange() {
      if (this._motionQuery.matches) this._onGlassPointerLeave();
      if (this._motionQuery.matches && this._video && !this._video.paused) {
        this._video.pause();
      } else {
        this._syncVideoPlayback();
      }
    }

    _onVisibilityChange() {
      this._syncVideoPlayback();
    }
  }

  if (!customElements.get('rs8-series-two')) {
    customElements.define('rs8-series-two', RS8SeriesTwo);
  }
})();
